/* eslint-disable @typescript-eslint/naming-convention */

// HHS V08 clinical condition categories (HCC code -> label).
// Source: CY2025 HHS-HCC DIY tables (Table 4, Version 08), columns 'V08 HCC' and 'HCC Label'.
// OR-10574 - carried forward from categoryMappingHHS_V07 with the v08 updates.

const categoryMappingHHS_V08: Record<string, string> = {
  1: 'HIV/AIDS',
  2: 'Septicemia, Sepsis, Systemic Inflammatory Response Syndrome/Shock',
  3: 'Central Nervous System Infections, Except Viral Meningitis',
  4: 'Viral or Unspecified Meningitis',
  6: 'Opportunistic Infections',
  8: 'Metastatic Cancer',
  9: 'Lung, Brain, and Other Severe Cancers, Including Pediatric Acute Lymphoid Leukemia',
  10: 'Non-Hodgkin Lymphomas and Other Cancers and Tumors',
  11: 'Colorectal, Breast (Age < 50), Kidney, and Other Cancers',
  12: 'Breast (Age 50+) and Prostate Cancer, Benign/Uncertain Brain Tumors, and Other Cancers and Tumors',
  13: 'Thyroid Cancer, Melanoma, Neurofibromatosis, and Other Cancers and Tumors',
  18: 'Pancreas Transplant Status',
  19: 'Diabetes with Acute Complications',
  20: 'Diabetes with Chronic Complications',
  21: 'Diabetes without Complication',
  22: 'Type 1 Diabetes Mellitus, add-on to Diabetes HCCs 19-21',
  23: 'Protein-Calorie Malnutrition',
  26: 'Mucopolysaccharidosis',
  27: 'Lipidoses and Glycogenosis',
  28: 'Congenital Metabolic Disorders, Not Elsewhere Classified',
  29: 'Amyloidosis, Porphyria, and Other Metabolic Disorders',
  30: 'Adrenal, Pituitary, and Other Significant Endocrine Disorders',
  34: 'Liver Transplant Status/Complications',
  '35.1': 'Acute Liver Failure/Disease, Including Neonatal Hepatitis',
  '35.2': 'Chronic Liver Failure/End-Stage Liver Disorders',
  36: 'Cirrhosis of Liver',
  '37.1': 'Chronic Viral Hepatitis C',
  '37.2': 'Chronic Hepatitis, Except Chronic Viral Hepatitis C',
  41: 'Intestine Transplant Status/Complications',
  42: 'Peritonitis/Gastrointestinal Perforation/Necrotizing Enterocolitis',
  45: 'Intestinal Obstruction',
  46: 'Chronic Pancreatitis',
  47: 'Acute Pancreatitis',
  48: 'Inflammatory Bowel Disease',
  54: 'Necrotizing Fasciitis',
  55: 'Bone/Joint/Muscle Infections/Necrosis',
  56: 'Rheumatoid Arthritis and Specified Autoimmune Disorders',
  57: 'Systemic Lupus Erythematosus and Other Autoimmune Disorders',
  61: 'Osteogenesis Imperfecta and Other Osteodystrophies',
  62: 'Congenital/Developmental Skeletal and Connective Tissue Disorders',
  63: 'Cleft Lip/Cleft Palate',
  64: 'Major Congenital Anomalies of Diaphragm, Abdominal Wall, and Esophagus, Age < 2',
  66: 'Hemophilia',
  67: 'Myelodysplastic Syndromes and Myelofibrosis',
  68: 'Aplastic Anemia',
  69: 'Acquired Hemolytic Anemia, Including Hemolytic Disease of Newborn',
  70: 'Sickle Cell Anemia (Hb-SS) and Thalassemia Beta Zero',
  71: 'Sickle-Cell Disorders, Except Sickle-Cell Anemia (Hb-SS) and Thalassemia Beta Zero; Beta Thalassemia Major',
  73: 'Combined and Other Severe Immunodeficiencies',
  74: 'Disorders of the Immune Mechanism',
  75: 'Coagulation Defects and Other Specified Hematological Disorders',
  81: 'Drug Use with Psychotic Complications',
  82: 'Drug Use Disorder, Moderate/Severe, or Drug Use with Non-Psychotic Complications',
  83: 'Alcohol Use with Psychotic Complications',
  84: 'Alcohol Use Disorder, Moderate/Severe, or Alcohol Use with Specified Non-Psychotic Complications',
  '87.1': 'Schizophrenia',
  '87.2': 'Delusional and Other Specified Psychotic Disorders, Unspecified Psychosis',
  88: 'Major Depressive Disorder, Severe, and Bipolar Disorders',
  90: 'Personality Disorders',
  94: 'Anorexia/Bulimia Nervosa',
  96: 'Prader-Willi, Patau, Edwards, and Autosomal Deletion Syndromes',
  97: 'Down Syndrome, Fragile X, Other Chromosomal Anomalies, and Congenital Malformation Syndromes',
  102: 'Autistic Disorder',
  103: 'Pervasive Developmental Disorders, Except Autistic Disorder',
  106: 'Traumatic Complete Lesion Cervical Spinal Cord',
  107: 'Quadriplegia',
  108: 'Traumatic Complete Lesion Dorsal Spinal Cord',
  109: 'Paraplegia',
  110: 'Spinal Cord Disorders/Injuries',
  111: 'Amyotrophic Lateral Sclerosis and Other Anterior Horn Cell Disease',
  112: 'Quadriplegic Cerebral Palsy',
  113: 'Cerebral Palsy, Except Quadriplegic',
  114: 'Spina Bifida and Other Brain/Spinal/Nervous System Congenital Anomalies',
  115: 'Myasthenia Gravis/Myoneural Disorders and Guillain-Barre Syndrome/Inflammatory and Toxic Neuropathy',
  117: 'Muscular Dystrophy',
  118: 'Multiple Sclerosis',
  119: 'Parkinson\'s, Huntington\'s, and Spinocerebellar Disease, and Other Neurodegenerative Disorders',
  120: 'Seizure Disorders and Convulsions',
  121: 'Hydrocephalus',
  122: 'Nontraumatic Coma, Except Diabetic, Hepatic, or Hypoglycemic; Nontraumatic Brain Compression/Anoxic Damage',
  123: 'Narcolepsy and Cataplexy',
  125: 'Respirator Dependence/Tracheostomy Status',
  126: 'Respiratory Arrest',
  127: 'Cardio-Respiratory Failure and Shock, Including Respiratory Distress Syndromes',
  128: 'Heart Assistive Device/Artificial Heart',
  129: 'Heart Transplant Status/Complications',
  130: 'Heart Failure',
  131: 'Acute Myocardial Infarction',
  132: 'Unstable Angina and Other Acute Ischemic Heart Disease',
  135: 'Heart Infection/Inflammation, Except Rheumatic',
  137: 'Hypoplastic Left Heart Syndrome and Other Severe Congenital Heart Disorders',
  138: 'Major Congenital Heart/Circulatory Disorders',
  139: 'Atrial and Ventricular Septal Defects, Patent Ductus Arteriosus, and Other Congenital Heart/Circulatory Disorders',
  142: 'Specified Heart Arrhythmias',
  145: 'Intracranial Hemorrhage',
  146: 'Ischemic or Unspecified Stroke',
  149: 'Cerebral Aneurysm and Arteriovenous Malformation',
  150: 'Hemiplegia/Hemiparesis',
  151: 'Monoplegia, Other Paralytic Syndromes',
  153: 'Atherosclerosis of the Extremities with Ulceration or Gangrene',
  154: 'Vascular Disease with Complications',
  156: 'Pulmonary Embolism and Deep Vein Thrombosis',
  158: 'Lung Transplant Status/Complications',
  159: 'Cystic Fibrosis',
  160: 'Chronic Obstructive Pulmonary Disease, Including Bronchiectasis',
  '161.1': 'Severe Asthma',
  '161.2': 'Asthma, Except Severe',
  162: 'Fibrosis of Lung and Other Lung Disorders',
  163: 'Aspiration and Specified Bacterial Pneumonias and Other Severe Lung Infections',
  174: 'Exudative Macular Degeneration',
  183: 'Kidney Transplant Status/Complications',
  184: 'End Stage Renal Disease',
  187: 'Chronic Kidney Disease, Stage 5',
  188: 'Chronic Kidney Disease, Severe (Stage 4)',
  203: 'Ectopic and Molar Pregnancy',
  204: 'Miscarriage with Complications',
  205: 'Miscarriage with No or Minor Complications',
  207: 'Pregnancy with Delivery with Major Complications',
  208: 'Pregnancy with Delivery with Complications',
  209: 'Pregnancy with Delivery with No or Minor Complications',
  210: '(Ongoing) Pregnancy without Delivery with Major Complications',
  211: '(Ongoing) Pregnancy without Delivery with Complications',
  212: '(Ongoing) Pregnancy without Delivery with No or Minor Complications',
  217: 'Chronic Ulcer of Skin, Except Pressure',
  218: 'Extensive Third Degree Burns',
  219: 'Major Skin Burn or Condition',
  223: 'Severe Head Injury',
  226: 'Hip and Pelvic Fractures',
  228: 'Vertebral Fractures without Spinal Cord Injury',
  234: 'Traumatic Amputations and Amputation Complications',
  242: 'Extremely Immature Newborns, Birthweight < 500 Grams',
  243: 'Extremely Immature Newborns, Including Birthweight 500-749 Grams',
  244: 'Extremely Immature Newborns, Including Birthweight 750-999 Grams',
  245: 'Premature Newborns, Including Birthweight 1000-1499 Grams',
  246: 'Premature Newborns, Including Birthweight 1500-1999 Grams',
  247: 'Premature Newborns, Including Birthweight 2000-2499 Grams',
  248: 'Other Premature, Low Birthweight, Malnourished, or Multiple Birth Newborns',
  249: 'Term or Post-Term Singleton Newborn, Normal or High Birthweight',
  251: 'Stem Cell, Including Bone Marrow, Transplant Status/Complications',
  253: 'Artificial Openings for Feeding or Elimination',
  254: 'Amputation Status, Upper Limb or Lower Limb',
}

export default categoryMappingHHS_V08
